import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { RootState } from '@/app/store';
import ButtonClose from '@/assets/images/icon_close.svg';
import ButtonReturn from '@/assets/images/icon_return.svg';
import { setHeaderHeight } from '@/features/drawer/drawerSlice';
import { useGetDrawerPath } from '@/utils/useGetDrawerPath';
import { useGetElementProperty } from '@/utils/useGetElementProperty';

export const Drawer = ({
  isCloseButton,
  backButtonFlg,
  children,
  title,
  isFullScreen,
  isOpen,
  closeDrawer,
  backDrawer,
  'data-testid': dataTestId,
  width,
  overlapNavigation = false,
}: {
  isCloseButton?: boolean;
  backButtonFlg?: boolean;
  children?: ReactNode;
  title: string;
  isFullScreen?: boolean;
  isLoading?: boolean;
  isOpen: boolean;
  closeDrawer?: () => void;
  backDrawer?: () => void;
  className?: string;
  'data-testid'?: string;
  width?: string;
  overlapNavigation?: boolean;
}) => {
  const navigate = useNavigate();
  const { navigationHeight, modeToastHeight } = useSelector(
    (state: RootState) => state.drawer
  );
  const dispatch = useDispatch();
  const targetRef = useRef<HTMLHeadingElement>(null);
  const headerHeight = useRef<number>();
  const { getElementProperty } =
    useGetElementProperty<HTMLHeadingElement>(targetRef);
  const { isPostMode } = useSelector((state: RootState) => state.post);
  const { isPreviewMode } = useSelector(
    (state: RootState) => state.authenticator
  );
  const style = {
    height: overlapNavigation ? '100dvh' : `calc(100% - ${navigationHeight}px)`,
    paddingTop: isPostMode || isPreviewMode ? modeToastHeight : '',
    boxSizing: 'border-box' as const,
    maxWidth: width,
  };
  const prevPath = useGetDrawerPath();

  useEffect(() => {
    headerHeight.current = getElementProperty('height');
    dispatch(setHeaderHeight(headerHeight.current));
  }, [dispatch, getElementProperty]);

  const handleBack = () => {
    if (backDrawer) {
      backDrawer();

      return;
    }

    prevPath === undefined ? navigate(-1) : navigate(prevPath);
  };

  return (
    <>
      {isOpen && (
        <div
          className={
            `fixed left-0 m-0 p-0 w-full md:max-w-[408px] ${
              overlapNavigation ? 'z-40' : 'z-20'
            }` +
            (isFullScreen === false ? ' rounded-t-[0.5rem]' : '') +
            (isOpen ? ' top-0' : ' duration-100 ease-linear transition-all')
          }
          {...{ style }}
          // TODO: 後ほど対応
          // @keydown.esc="closeDrawer"
        >
          <header
            ref={targetRef}
            className="flex h-[48px] w-full items-center justify-center bg-white px-[8px]"
            data-testid={
              dataTestId != undefined ? `${dataTestId}-title` : undefined
            }
          >
            <div className="flex w-full items-center justify-between">
              {isCloseButton === true ? (
                <>
                  {backButtonFlg === true && (
                    <button
                      className="return group/help-back"
                      onClick={handleBack}
                      aria-label="一つ前に戻る"
                    >
                      <figure className="min-w-[40px] duration-300 group-hover/help-back:-translate-x-2">
                        <img src={ButtonReturn} alt="一つ前に戻る" />
                      </figure>
                    </button>
                  )}
                  <p className="container mx-auto truncate text-center text-base font-bold">
                    {title}
                  </p>
                  <button
                    className="close group"
                    onClick={closeDrawer}
                    aria-label="閉じる"
                  >
                    <figure className="min-w-[40px] duration-300 group-hover:scale-125">
                      <img src={ButtonClose} alt="閉じる" />
                    </figure>
                  </button>
                </>
              ) : (
                <>
                  {backButtonFlg === true && (
                    <button
                      className="return group/help-back"
                      onClick={handleBack}
                      aria-label="一つ前に戻る"
                    >
                      <figure className="min-w-[40px] duration-300 group-hover/help-back:-translate-x-2">
                        <img src={ButtonReturn} alt="一つ前に戻る" />
                      </figure>
                    </button>
                  )}
                  <p className="container mx-auto truncate text-center text-base font-bold">
                    {title}
                  </p>
                </>
              )}
            </div>
          </header>
          <div className="border-t"></div>
          {children}
        </div>
      )}
    </>
  );
};
